import React from 'react';
import { Button, Space, Tag, Tooltip, Typography, App } from 'antd';
import { DownloadOutlined, EyeOutlined, PaperClipOutlined } from '@ant-design/icons';
import { documentService } from '@/services/document.service';
import type { WorkOrderBoqLineDocumentsLine } from '@/services/workOrder.service';

const { Text } = Typography;

export type WoBoqLineTechnicalDocumentsPanelProps = {
  row?: WorkOrderBoqLineDocumentsLine;
  /** When false, hide open/download (manufacturing.view required on API). */
  canView?: boolean;
  /** Compact header for collapse panels. */
  compact?: boolean;
};

function categoryLabel(category: string | null | undefined): string {
  const c = String(category ?? '').trim().toLowerCase();
  if (c === 'technical') return 'Technical';
  if (c === 'attachment') return 'Attachments';
  return category?.trim() || 'File';
}

function categoryColor(category: string | null | undefined): string {
  const c = String(category ?? '').trim().toLowerCase();
  if (c === 'technical') return 'blue';
  if (c === 'attachment') return 'default';
  return 'default';
}

/**
 * Read-only BOQ line attachments for WO line items
 * (`documents.entity_type = BOQ_LINE_ITEM`, categories Attachment + Technical).
 * Same source as BOQ Builder → Line edit → Attachments / Technical attachments.
 */
export const WoBoqLineTechnicalDocumentsPanel: React.FC<WoBoqLineTechnicalDocumentsPanelProps> = ({
  row,
  canView = true,
  compact = false,
}) => {
  const { message } = App.useApp();
  const attachments = row?.attachments ?? [];
  const n = attachments.length;

  if (!canView) {
    return (
      <Text type="secondary" style={{ fontSize: 12 }}>
        Needs manufacturing view to open BOQ line documents
      </Text>
    );
  }

  const sourceLabel =
    row?.boq_line_source === 'persisted'
      ? 'WO snapshot'
      : row?.boq_line_source === 'resolved_from_quotation'
        ? 'Quotation / BOQ chain'
        : row?.boq_line_item_id
          ? 'Estimation'
          : null;

  return (
    <div style={{ marginTop: compact ? 8 : 0 }}>
      <Space wrap size={8} style={{ marginBottom: 6 }}>
        <Text strong style={{ fontSize: compact ? 12 : 13 }}>
          <PaperClipOutlined style={{ marginRight: 6 }} />
          Line attachments
        </Text>
        <Tag color={n > 0 ? 'green' : 'default'}>
          {n} file{n === 1 ? '' : 's'}
        </Tag>
        {sourceLabel ? (
          <Text type="secondary" style={{ fontSize: 11 }}>
            {sourceLabel}
          </Text>
        ) : null}
      </Space>

      {!row?.boq_line_item_id ? (
        <Text type="secondary" style={{ fontSize: 12 }}>
          No BOQ line link — documents from estimation line edit are unavailable for this WO line.
        </Text>
      ) : n === 0 ? (
        <Text type="secondary" style={{ fontSize: 12 }}>
          No attachments or technical attachments on this BOQ estimation line.
        </Text>
      ) : (
        <Space direction="vertical" size={4} style={{ width: '100%' }}>
          {attachments.map((a) => {
            const label = a.file_name || a.document_name || 'Document';
            return (
              <div
                key={a.id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  width: '100%',
                  minWidth: 0,
                }}
              >
                <Tag
                  color={categoryColor(a.document_category)}
                  style={{ marginInlineEnd: 0, flex: '0 0 auto' }}
                >
                  {categoryLabel(a.document_category)}
                </Tag>
                <span
                  title={label}
                  style={{
                    flex: '1 1 auto',
                    minWidth: 0,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    fontSize: 12,
                  }}
                >
                  {label}
                </span>
                <span style={{ flex: '0 0 auto', display: 'inline-flex', gap: 2 }}>
                  <Tooltip title="View">
                    <Button
                      type="link"
                      size="small"
                      icon={<EyeOutlined />}
                      style={{ padding: 0 }}
                      onClick={async () => {
                        try {
                          await documentService.openDocumentInNewTab(a.id);
                        } catch (err: unknown) {
                          const msg =
                            err instanceof Error && err.message ? err.message : 'Could not open document';
                          message.error(msg);
                        }
                      }}
                    />
                  </Tooltip>
                  <Tooltip title="Download">
                    <Button
                      type="link"
                      size="small"
                      icon={<DownloadOutlined />}
                      style={{ padding: 0 }}
                      onClick={async () => {
                        try {
                          const blob = await documentService.downloadDocument(a.id);
                          if (blob.type?.includes('application/json')) {
                            const text = await blob.text();
                            let msg = 'Download failed';
                            try {
                              const parsed = JSON.parse(text) as { message?: string };
                              if (parsed?.message) msg = parsed.message;
                            } catch {
                              /* ignore */
                            }
                            message.error(msg);
                            return;
                          }
                          const url = window.URL.createObjectURL(blob);
                          const el = document.createElement('a');
                          el.href = url;
                          el.download = label;
                          el.click();
                          window.setTimeout(() => window.URL.revokeObjectURL(url), 2000);
                        } catch {
                          message.error('Download failed');
                        }
                      }}
                    />
                  </Tooltip>
                </span>
              </div>
            );
          })}
        </Space>
      )}
    </div>
  );
};

export default WoBoqLineTechnicalDocumentsPanel;
